import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery } from '@tanstack/react-query';
import type { AiReviewResult, AssessorReviews } from '@grc/types';
import { apiFetch, ApiRequestError } from '../../lib/api.js';
import { useOrg } from '../../lib/auth.js';
import {
  Badge,
  Button,
  Card,
  EmptyState,
  LoadingPane,
  PageHeader,
  Table,
  useToast,
} from '../../components/ui.js';

/** Per-response AI verdict, keyed by responseId. */
type Verdicts = Record<string, AiReviewResult>;

export function AssessorReviewsPage() {
  const { org, orgs: knownOrgs } = useOrg();
  const { toast } = useToast();
  const isAssessor = org.role === 'assessor';
  // The queue spans every organisation the assessor is assigned to; each
  // carries its own AI switch (/auth/me assessorOrgs.aiEnabled). Unknown
  // organisations keep the column — the server refuses with 403 ai_disabled.
  const aiEnabledFor = (orgId: string): boolean =>
    knownOrgs.find((o) => o.orgId === orgId)?.aiEnabled ?? true;
  const [verdicts, setVerdicts] = useState<Verdicts>({});
  const [runningId, setRunningId] = useState<string | null>(null);

  const reviews = useQuery({
    queryKey: ['assessor-reviews'],
    queryFn: () => apiFetch<AssessorReviews>('/assessor/reviews'),
    enabled: isAssessor,
  });

  const aiReview = useMutation({
    mutationFn: (responseId: string) =>
      apiFetch<AiReviewResult>(`/responses/${responseId}/ai-review`, {
        method: 'POST',
        body: { postComment: true },
      }),
    onSuccess: (result, responseId) => {
      setVerdicts((v) => ({ ...v, [responseId]: result }));
      toast(
        result.verdict === 'pass'
          ? 'AI pre-check: looks ready for sign-off.'
          : result.commentId
            ? 'AI pre-check flagged gaps — a comment was sent to the author to revise.'
            : 'AI pre-check flagged gaps.',
        result.verdict === 'pass' ? 'success' : 'default',
      );
    },
    onError: (err: unknown) => {
      if (err instanceof ApiRequestError && err.code === 'not_configured') {
        toast('AI review is not configured on this environment (OPENAI_API_KEY required).', 'error');
        return;
      }
      toast(err instanceof ApiRequestError ? err.message : 'AI review failed', 'error');
    },
    onSettled: () => setRunningId(null),
  });

  if (!isAssessor) {
    return <EmptyState title="Assessors only" message="Assigned assessors review organisations here." />;
  }
  if (reviews.isLoading) return <LoadingPane label="Loading your review queue" />;
  const orgs = reviews.data?.orgs ?? [];

  return (
    <div>
      <PageHeader
        title="Items to review"
        subtitle="Finalised responses awaiting your assessment, grouped by organisation. Run an AI pre-check to flag anything critically missing before you sign off — it goes back to the author as a comment to revise."
      />

      {orgs.length === 0 ? (
        <EmptyState
          title="Nothing to review"
          message="You're all caught up — finalised responses awaiting your sign-off will appear here."
        />
      ) : (
        orgs.map((group) => {
          const aiOn = aiEnabledFor(group.orgId);
          return (
            <Card key={group.orgId} title={`${group.orgName} · ${group.items.length} to review`}>
              <Table
                head={
                  <>
                    <th>Requirement</th>
                    <th>Assessment</th>
                    {aiOn && <th>AI pre-check</th>}
                    <th />
                  </>
                }
              >
                {group.items.map((item) => {
                  const verdict = verdicts[item.responseId];
                  return (
                    <tr key={item.responseId}>
                      <td>
                        <div style={{ fontWeight: 600 }}>
                          {item.clauseNumber} · {item.clauseTitle}
                        </div>
                        <div style={{ color: 'var(--color-text-muted)', fontSize: 'var(--text-xs)' }}>
                          {item.questionText}
                        </div>
                        {!item.hasContent && (
                          <Badge tone="danger">empty response</Badge>
                        )}
                      </td>
                      <td>
                        <div>{item.assessmentName}</div>
                        <div style={{ color: 'var(--color-text-faint)', fontSize: 'var(--text-xs)' }}>
                          {item.standardName}
                        </div>
                      </td>
                      {aiOn && (
                        <td style={{ maxWidth: 320 }}>
                          {verdict ? (
                            <div>
                              <Badge tone={verdict.verdict === 'pass' ? 'success' : 'warning'}>
                                {verdict.verdict === 'pass' ? 'Looks ready' : 'Needs revision'}
                              </Badge>
                              <div style={{ fontSize: 'var(--text-xs)', marginTop: 4 }}>{verdict.summary}</div>
                              {verdict.criticalGaps.length > 0 && (
                                <ul
                                  style={{
                                    margin: '6px 0 0',
                                    paddingLeft: 16,
                                    fontSize: 'var(--text-xs)',
                                    color: 'var(--color-text-muted)',
                                  }}
                                >
                                  {verdict.criticalGaps.map((g, i) => (
                                    <li key={i}>{g}</li>
                                  ))}
                                </ul>
                              )}
                              {verdict.commentId && (
                                <div style={{ color: 'var(--color-success)', fontSize: 'var(--text-xs)', marginTop: 4 }}>
                                  ✓ sent to author as a comment
                                </div>
                              )}
                            </div>
                          ) : (
                            <span style={{ color: 'var(--color-text-faint)', fontSize: 'var(--text-xs)' }}>
                              not run
                            </span>
                          )}
                        </td>
                      )}
                      <td>
                        <div style={{ display: 'flex', gap: 8, flexDirection: 'column' }}>
                          {aiOn && (
                            <Button
                              size="sm"
                              variant="secondary"
                              loading={runningId === item.responseId && aiReview.isPending}
                              disabled={aiReview.isPending}
                              onClick={() => {
                                setRunningId(item.responseId);
                                aiReview.mutate(item.responseId);
                              }}
                            >
                              AI pre-check
                            </Button>
                          )}
                          <Link
                            to={`/assessments/${item.assessmentId}/responses/${item.responseId}`}
                            style={{ fontSize: 'var(--text-xs)' }}
                          >
                            Open response →
                          </Link>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </Table>
            </Card>
          );
        })
      )}
    </div>
  );
}
